import { t } from "i18next"
import { useEffect, useRef, useContext } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"

import { ConsoleLogger } from "src/util/logger"
import { useIsMobileApp } from "src/util/deviceType"
import { AuthedUser } from "src/recoil/atoms/authedUser"
import {
  chatIsReadSelector,
  currentChatGenerationAtom
} from "src/recoil/atoms/chats"
import {
  TrulienceStateCallbackSelector,
  TrulienceStateUpdater
} from "src/recoil/atoms/trulience"
import { showDevicePermissionOverlayAtom } from "src/recoil/atoms/settings"
import { SDKContext } from "src/context/SDKContext"
import useOverlayControl from "src/hooks/useOverlayControl"
import { InspirationModel } from "src/components/views/avatars/components/Inspirations"

import useCurrentAvatarInfo from "./useCurrentAvatarInfo"
import {
  avatarCommunication,
  AvatarEvent,
  AvatarMessageType
} from "../AvatarCommunication"
import { queuedInspirationAtom } from "src/recoil/atoms/inspirations"
import { useHistory } from "react-router-dom"
import useConfig from "src/hooks/useConfig"

const logger = new ConsoleLogger()

type micAccessHandlerResponseType =
  | boolean
  | { permissionGranted: boolean; state: "granted" | "prompt" | "denied" }

const ignoreMessages = [
  "<trl-state enableListening='true' />",
  "<trl-state enableListening='false' />",
  "<trl-info processingInput='true' />",
  "<trl-content orientation='portrait' />",
  "<trl-content orientation='landscape' />",
  "<trl-enable-joystick />",
  "<trl-disable-joystick />"
]

const useTrulienceAvatarEventListener = (
  avatarInitialised: boolean,
  {
    queuedInspiration,
    authedUser
  }: { queuedInspiration: InspirationModel; authedUser: AuthedUser }
) => {
  // Try to use only setter state
  const avatarPhotoRef = useRef(null)
  const avatarConfig = useCurrentAvatarInfo()
  const meetingClient = useContext(SDKContext)
  const setChatIsRead = useSetRecoilState(chatIsReadSelector)
  const { allowAllOverlays, hideAllOverlays } = useOverlayControl()
  const updateTrulienceState = useSetRecoilState(TrulienceStateUpdater)
  const trulienceStateCallback = useRecoilValue(TrulienceStateCallbackSelector)
  const setShowDevicePermission = useSetRecoilState(
    showDevicePermissionOverlayAtom
  )
  const setQueuedInspiration = useSetRecoilState(queuedInspirationAtom)
  const history = useHistory()
  const config = useConfig()
  const generation = useRecoilValue(currentChatGenerationAtom)

  /** Auth Handlers */
  const handleOnAuthSuccess = (eventParams: { avatarPhotoURL: string }) => {
    // Set the avatar photo url
    if (eventParams.avatarPhotoURL) {
      avatarPhotoRef.current = eventParams.avatarPhotoURL
      updateTrulienceState({ avatarPhotoUrl: eventParams.avatarPhotoURL })
    }
  }

  /** Websocket Handlers */
  const handleWebsocketConnect = () => {
    // send wake msg
    if (!queuedInspiration) {
      const additionalInstruction =
        "These are facts that you know about the user but do not need to mention unless asked for or found to be relevant to the current conversation. Greet the user with a random greeting, do not mention the persona."
      if (!authedUser.screenName) {
        avatarCommunication.sendMessage(t("Hi"))
      } else if (authedUser.screenName && !authedUser.personaDescription) {
        avatarCommunication.sendMessage(
          t("This user's name is") +
            " " +
            authedUser.screenName +
            ". " +
            additionalInstruction
        )
      } else {
        avatarCommunication.sendMessage(
          t("This user's name is") +
            ". " +
            authedUser.screenName +
            (!!authedUser.personaDescription
              ? `. ${t("Their persona can be described as:")} ${
                  authedUser.personaDescription
                }`
              : "") +
            " " +
            additionalInstruction
        )
      }
    } else if (!!queuedInspiration) {
      meetingClient?.sendPublicChat(
        !!queuedInspiration.chatContent
          ? queuedInspiration.chatContent
          : queuedInspiration.title,
        null,
        null,
        null,
        avatarConfig?.id?.toString(),
        generation
      )
      avatarCommunication.sendMessage(queuedInspiration.prompt)
      setQueuedInspiration(null)
    }
  }

  const handleWebsocketClose = () => {
    history.replace(config.postLoginRedirectUrl)
  }

  const handleWebsocketMessage = eventParams => {
    logger.log("Websocket message:", eventParams)

    if (eventParams.messageType == AvatarMessageType.ChatText) {
      // if (typeof eventData.message == "string" && (eventData.message as string).startsWith("<trl")) return
      let message = eventParams.messageArray[0].message

      if (ignoreMessages.includes(message)) return
      // remove <trl-content queue='true' /> and the blank space that comes after it
      message = message.replace(/^<trl-[^>]*\/>\s*/, "").trim()
      if (message === "") return
      const senderType = eventParams.senderType // TODO: handle DH's new senderType once its fixed
      if (senderType === "AVATAR" || senderType !== "USER") {
        meetingClient?.sendPublicChatAsAvatar(
          message,
          avatarConfig?.id?.toString(),
          avatarConfig.avatarName,
          avatarPhotoRef.current,
          generation
        )
      } else {
        // Handle user voice response
        meetingClient?.sendPublicChat(
          message,
          null,
          null,
          null,
          avatarConfig?.id?.toString(),
          generation
        )
      }
      setChatIsRead(true)
    }
  }

  /** Media Handlers */
  const handleMediaConnected = async () => {
    console.log("In callback mediaConnectedHandler")
    // Enable mic and speaker if you want to enable it by default
    avatarCommunication.fixAudioContext()

    const trulienceState = await trulienceStateCallback.getTrulienceState()
    avatarCommunication.setMicStatus(trulienceState.micEnabled)
    avatarCommunication.setSpeakerStatus(trulienceState.speakerEnabled)
  }

  const handleMicAccess = (resp: micAccessHandlerResponseType) => {
    logger.log("In callback micAccessHandler resp = ", resp)
    if (typeof resp === "boolean") {
      updateTrulienceState({ micEnabled: resp })
      return
    }

    updateTrulienceState({
      micEnabled: resp.permissionGranted,
      micPermissionState: resp.state
    })

    if (resp.state === "denied") {
      const isMobileApp = useIsMobileApp()

      if (isMobileApp) {
        meetingClient?.nativeBridge.eventMicAccessPermissionRequired()
      } else {
        setShowDevicePermission({
          state: "denied",
          type: "microphone"
        })
      }
    }
  }

  const handleMicStatus = (status: boolean) => {
    avatarCommunication.fixAudioContext()
    updateTrulienceState({ micEnabled: status })
  }

  const handleSpeakerStatus = (status: boolean) => {
    avatarCommunication.fixAudioContext()
    updateTrulienceState({ speakerEnabled: status })
  }

  /** Avatar Event Handlers */
  const handleAvatarLoadProgress = (resp: { percent: number }) => {
    if (resp.percent === 1) {
      logger.log("Avatar has been successfully loaded ", resp.percent)
      meetingClient?.nativeBridge?.eventAvatarLoaded()
    }
  }

  useEffect(() => {
    if (!avatarInitialised) return
    const iframeElement = document.getElementById(
      "trl-iframe"
    ) as HTMLIFrameElement
    avatarCommunication.init(iframeElement)
    updateTrulienceState({ iframeElement })

    avatarCommunication.on(AvatarEvent.WebsocketConnect, handleWebsocketConnect)
    avatarCommunication.on(AvatarEvent.WebsocketClose, handleWebsocketClose)
    avatarCommunication.on(AvatarEvent.MicUpdate, handleMicStatus)
    avatarCommunication.on(AvatarEvent.SpeakerUpdate, handleSpeakerStatus)
    avatarCommunication.on(AvatarEvent.AuthSuccess, handleOnAuthSuccess)
    avatarCommunication.on(AvatarEvent.WebsocketMessage, handleWebsocketMessage)
    avatarCommunication.on(AvatarEvent.MediaConnected, handleMediaConnected)
    avatarCommunication.on(AvatarEvent.MicAccess, handleMicAccess)
    avatarCommunication.on(AvatarEvent.ModalDialogOpen, hideAllOverlays)
    avatarCommunication.on(AvatarEvent.ModalDialogClose, allowAllOverlays)
    avatarCommunication.on(
      AvatarEvent.AvatarLoadProgress,
      handleAvatarLoadProgress
    )

    return () => {
      avatarCommunication.off(
        AvatarEvent.WebsocketConnect,
        handleWebsocketConnect
      )
      avatarCommunication.off(AvatarEvent.MicUpdate, handleMicStatus)
      avatarCommunication.off(AvatarEvent.SpeakerUpdate, handleSpeakerStatus)
      avatarCommunication.off(AvatarEvent.AuthSuccess, handleOnAuthSuccess)
      avatarCommunication.off(
        AvatarEvent.WebsocketMessage,
        handleWebsocketMessage
      )
      avatarCommunication.off(AvatarEvent.MicAccess, handleMicAccess)
      avatarCommunication.off(AvatarEvent.ModalDialogOpen, hideAllOverlays)
      avatarCommunication.off(AvatarEvent.ModalDialogClose, allowAllOverlays)
      avatarCommunication.off(AvatarEvent.MediaConnected, handleMediaConnected)
      avatarCommunication.off(
        AvatarEvent.AvatarLoadProgress,
        handleAvatarLoadProgress
      )
      avatarCommunication.reset()
    }
  }, [avatarInitialised])
}

export default useTrulienceAvatarEventListener
